// format - display-formatting helpers (bytes, relative time, duration) used across pages.

import { isContainerLane } from './lanes';

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

export function formatRelativeTime(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

export function formatDuration(secs: number): string {
  if (secs < 60) return `${secs.toFixed(1)}s`;
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}m ${s}s`;
}

// formatDuration's "--for-invalid" sibling, for upload ETA cells whose
// input may be NaN/Infinity early in the run.
export function formatEta(secs: number): string {
  if (!Number.isFinite(secs) || secs <= 0) return '-';
  if (secs < 60) return `${Math.round(secs)}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  return `${(secs / 3600).toFixed(1)}h`;
}

/**
 * How a process is named on screen.
 *
 * The capture does not always learn a name: a process already running when
 * tracing began, or one that exited before its image was resolved, arrives with
 * an empty one. Rendering that raw produced a row with a pid and a blank where
 * the name should be, which reads as a broken page rather than as the honest
 * "we saw this process but never learned what it was".
 */
export function processLabel(name: string | null | undefined, pid: number | null | undefined): string {
  const trimmed = (name ?? '').trim();
  if (trimmed) return trimmed;
  return pid == null ? 'unknown process' : `unknown (pid ${pid})`;
}

// shortId abbreviates a Voltz entity id for scanning in a dense table.
//
// An id is "<kind>-<26 random chars>". The body was a ULID until issue #59,
// which put a millisecond timestamp in the leading characters and meant two ids
// created the same second rendered identically once truncated. The body is
// random now, so the front of it is as distinctive as any other part and the
// abbreviation reads the way people expect.
//
// The kind prefix stays, because it is what makes an id self-describing.
export function shortId(s: string): string {
  if (s.length <= 12) return s;
  const m = /^([a-z]{3})-([0-9abcdefghjkmnpqrstvwxyz]{26})$/.exec(s);
  if (!m) return s.slice(0, 8);
  return `${m[1]}-${m[2].slice(0, 6)}`;
}

/**
 * A configured run timeout, at the size a table cell affords.
 *
 * One unit, floored: "2m", not "2m 0s". The configured budget is a round
 * number an operator chose, and the cell it rides in already carries the lane
 * and the OS.
 */
export function formatTimeoutShort(seconds: number): string {
  if (seconds <= 0) return '';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h`;
}

/**
 * Wall-clock time between two instants, for a submission row.
 *
 * End to end - submission to completion, queue wait included - because that is
 * the number an operator watching throughput actually experiences. A dash
 * while the run is still open: the list refreshes on its own interval, and a
 * ticking clock re-rendering every row would buy nothing the Status column
 * does not already say.
 */
export function formatWallDuration(startIso: string, endIso: string | null | undefined): string {
  if (!endIso) return '-';
  const secs = (new Date(endIso).getTime() - new Date(startIso).getTime()) / 1000;
  if (!Number.isFinite(secs) || secs < 0) return '-';
  if (secs < 60) return `${Math.round(secs)}s`;
  return `${Math.floor(secs / 60)}m ${Math.floor(secs % 60)}s`;
}

/**
 * The size of what was submitted, for a submission row.
 *
 * A URL analysis submitted no bytes. `sample_size` is the column's coalesced
 * zero on that lane, and formatting it renders "0 B" - a measurement of a file
 * that does not exist, in a column an analyst reads as one. Shared by the two
 * pages that list submissions so they cannot answer it differently.
 */
export function formatSubmittedSize(lane: string, sampleSize: number): string {
  return isContainerLane(lane) ? '-' : formatBytes(sampleSize);
}
